import * as React from 'react';
import GameBlock from '../../components/GameBlock/GameBlock.tsx';
import { GameType } from '../../types/GameType.ts';
import { getBlockColors } from '../../utils/visualUtils.ts';
interface GameRowProps {
  rowLength: number;
  word?: string;
  minWordLength?: number;
  maxWordLength?: number;
}

interface EnteredGameRowProps {
  rowLength: number;
  word: string;
  previousWord: string;
  endWord: string;
  gameType: GameType;
}

interface StartWordRowProps {
  rowLength: number;
  word: string;
}

interface EndWordRowProps {
  rowLength: number;
  word: string;
  lastGameWord: string;
  isBouncingBlock?: boolean;
}

interface ExampleGameRowProps {
  word: string;
}

class GameRow extends React.Component<GameRowProps> {
  static defaultProps = {
    word: '',
    minWordLength: null,
    maxWordLength: null,
  };

  render() {
    const word = this.props.word != null ? this.props.word : '';
    const blocks: React.ReactNode[] = [];
    const minWordLength = this.props.minWordLength !== null && this.props.minWordLength !== undefined
      ? this.props.minWordLength 
      : this.props.rowLength;
    const maxWordLength = this.props.maxWordLength !== null && this.props.maxWordLength !== undefined
      ? this.props.maxWordLength
      : this.props.rowLength;
    
    for (let i = 0; i < this.props.rowLength; i++) {
      const character = i < word.length ? word[i] : undefined;
      const classNames: string[] = [];      
      
      const isAboveMinWordLength = i >= minWordLength;
      const isBelowMaxWordLength = i < maxWordLength;
      if (character == null && isAboveMinWordLength && isBelowMaxWordLength) {
        classNames.push('optionalBlock');
      }

      // Add invisible block class for blocks above max length
      const isAboveMaxWordLength = i >= maxWordLength;
      if (isAboveMaxWordLength) {
        classNames.push('invisibleBlock');
      }
      
      // Then add filled block class if the block has a character
      // This ensures the filled style will override optional style
      if (character != null) {
        classNames.push('filledBlock');
      }
      
      // Add cursor position indicator
      if (i === word.length) {
        classNames.push('currentBlock');
      }
      
      blocks.push(
        <GameBlock 
          key={i} 
          character={character}
          classNames={classNames}
        />
      );
    }
    
    return (
      <div className='row'>
        {blocks}
      </div>
    );
  }
}

/**
 * Finds the index where two strings differ by exactly one character.
 * Returns undefined if:
 * - The strings are identical
 * - The strings differ by more than one character
 * - newStr is shorter than oldStr
 * 
 * @param oldStr The original string
 * @param newStr The new string that should be longer or equal length
 * @returns The index where the change occurred, or undefined if no valid change
 */
export function findChangeLocation(oldStr: string, newStr: string): number | undefined {
  // If strings are identical, no change
  if (oldStr === newStr) return undefined;
  
  // If new string is shorter than old string, invalid change
  if (newStr.length < oldStr.length) return undefined;
  
  // If one string is longer than the other by more than 1, invalid change
  if (Math.abs(oldStr.length - newStr.length) > 1) return undefined;
  
  // For strings of equal length, find the first different character
  if (oldStr.length === newStr.length) {
    for (let i = 0; i < oldStr.length; i++) {
      if (oldStr[i] !== newStr[i]) {
        return i;
      }
    }
  }
  
  // For strings differing by 1 character, find where the difference occurs
  for (let i = 0; i < oldStr.length; i++) {
    if (oldStr[i] !== newStr[i]) {
      return i;
    }
  }
  
  // If we get here, the difference must be at the end
  return oldStr.length;
} 

class EnteredGameRow extends React.Component<EnteredGameRowProps> {
  render() {
    const blocks: React.ReactNode[] = [];
    const changeLocation = findChangeLocation(this.props.previousWord, this.props.word);
    const { word, endWord, rowLength, gameType } = this.props;

    const colors = getBlockColors(word, endWord, gameType);

    for (let i = 0; i < rowLength; i++) {
      const classNames: string[] = [];
      const isWordBlock = i < word.length;
      if (isWordBlock) {
        classNames.push('filledBlock');
        if (i === changeLocation) {
          classNames.push('transitionBlock');
        }
        if (colors[i] === 'green') {
          classNames.push('greenBlock');
        } else if (colors[i] === 'yellow') {
          classNames.push('yellowBlock');
        }
      } else {
        classNames.push('invisibleBlock');
      }

      blocks.push(
        <GameBlock 
          key={i} 
          character={isWordBlock ? word[i] : undefined}
          classNames={classNames}
        />
      );
    }
    return (
      <div className='row'>
        {blocks}
      </div>
    );
  }
}

class StartWordRow extends React.Component<StartWordRowProps> {
  render() {
    const blocks: React.ReactNode[] = [];
    for (let i = 0; i < this.props.rowLength; i++) {
      if (i < this.props.word.length) {
        blocks.push(
          <GameBlock 
            key={i} 
            character={this.props.word[i]}
            classNames={['startWordBlock']}
            />
        );
      } else {
        blocks.push(
          <GameBlock 
            key={i} 
            classNames={['invisibleBlock']}
          />
        );
      }
    }
    return (
      <div className='row startWordRow'>
        {blocks}
      </div>
    );
  }
}

class EndWordRow extends React.Component<EndWordRowProps> {
  static defaultProps = {
    isBouncingBlock: false,
  };

  render() {
    const blocks: React.ReactNode[] = [];
    for (let i = 0; i < this.props.rowLength; i++) {
      if (i < this.props.word.length) {
        const isBlockComplete = this.props.word.charAt(i) === this.props.lastGameWord.charAt(i);
        blocks.push(
          <GameBlock 
            key={i} 
            character={this.props.word[i]}
            classNames={
              isBlockComplete ? (
                this.props.isBouncingBlock ? 
                  ['endWordBlockComplete', 'bouncingBlock'] :
                  ['endWordBlockComplete']
              ): ['endWordBlock']
            }
          />
        );
      } else {
        blocks.push(
          <GameBlock 
            key={i} 
            classNames={['invisibleBlock']}
          />
        );
      }
    }
    return (
      <div className='row endWordRow'>
        {blocks}
      </div>
    );
  }
}

class ExampleGameRow extends React.Component<ExampleGameRowProps> {
  render() {
    const blocks: React.ReactNode[] = [];
    for (let i = 0; i < this.props.word.length; i++) {
      blocks.push(
        <GameBlock 
          key={i} 
          character={this.props.word[i]}
          classNames={['exampleWordBlock']}
        />
      );
    }
    return (
      <div className='row'>
        {blocks}
      </div>
    );
  }
}

export {
  GameRow,
  EnteredGameRow,
  StartWordRow,
  EndWordRow,
  ExampleGameRow,
};